/*
 * Reads the static dataset generated by scripts/build-data.js.
 *
 * This replaces the CKAN calls the app used to make. The records handed back are
 * shaped exactly like the datastore rows they replace - same field names, same
 * `YYYY-MM-DDT00:00:00` dates - so the components consuming them did not have to
 * change how they read data.
 *
 * Everything is reached through the generated manifest's import() calls rather than
 * fetch(), because Parcel's dev server only serves what is in the module graph.
 * Each file is its own lazy chunk, so opening one country downloads one country.
 */

import * as manifest from './bundled/manifest.js';
import * as bundleIndex from './bundled/index.json';

const index = bundleIndex.default || bundleIndex;

// Parcel hands back a module namespace; the JSON sits on `default`.
const unwrap = mod => (mod && mod.default) || mod;

// One in-flight promise per file, so repeat visits to a country cost nothing.
const inFlight = new Map();
function once(key, load) {
    if (!inFlight.has(key)) inFlight.set(key, load().then(unwrap));
    return inFlight.get(key);
}

const DAY = 86400000;
const SUFFIX = 'T00:00:00';

/*
 * Series are stored as a start date plus a length, since they are contiguous daily.
 * Any missing days are listed in `gaps`.
 */
function datesOf(entry) {
    const gaps = entry.gaps ? new Set(entry.gaps) : null;
    const out = [];
    let t = Date.parse(entry.start + SUFFIX + 'Z');
    while (out.length < entry.n) {
        const iso = new Date(t).toISOString().slice(0, 10);
        if (!gaps || !gaps.has(iso)) out.push(iso);
        t += DAY;
    }
    return out;
}

/*
 * case_history is the trailing 14 values of new_cases_smoothed_per_million, written
 * as a pipe-delimited string with `nan` for gaps. It is derived here rather than
 * stored - it was over half the size of the case table.
 */
function caseHistory(series, i) {
    const out = [];
    for (let j = i - 13; j <= i; j++) {
        const v = j < 0 ? null : series[j];
        out.push(v == null ? 'nan' : String(v));
    }
    return out.join('|');
}

function assertKnown(what, name, available) {
    if (!available) {
        throw new Error(`Unknown ${what} "${name}" in the bundled data. ` +
            `Re-run scripts/build-data.js if the source exports changed.`);
    }
}

/** Every case row for a dataset - what the map, leaderboard and timeline read. */
export function loadCases(dataset) {
    assertKnown('dataset', dataset, manifest.cases[dataset]);
    return once(`cases:${dataset}`, manifest.cases[dataset]).then(data => {
        const records = [];
        for (const iso of Object.keys(data.countries)) {
            const c = data.countries[iso];
            const dates = datesOf(c);
            for (let i = 0; i < dates.length; i++) {
                records.push({
                    date: dates[i] + SUFFIX,
                    iso_code: iso,
                    location: c.location,
                    new_cases_smoothed: c.s[i],
                    new_cases_smoothed_per_million: c.m[i],
                    case_history: caseHistory(c.m, i)
                });
            }
        }
        return records;
    });
}

/** Every metric for one country, ordered by date - the old `SELECT * WHERE iso_code`. */
export function loadCountry(dataset, isoCode) {
    const countries = manifest.country[dataset];
    assertKnown('dataset', dataset, countries);
    if (!countries[isoCode]) return Promise.resolve([]);

    return once(`country:${dataset}:${isoCode}`, countries[isoCode]).then(data => {
        const dates = datesOf(data);
        const metrics = Object.keys(data.m);
        return dates.map((d, i) => {
            const row = { date: d + SUFFIX, iso_code: data.iso_code, ...data.static };
            for (const m of metrics) row[m] = data.m[m][i];
            return row;
        });
    });
}

/**
 * One metric across the given countries, within an inclusive date range - the old
 * `SELECT date, iso_code, location, <metric> WHERE date BETWEEN .. AND iso_code IN ..`.
 */
export function loadMetricRange(dataset, metric, isoCodes, from, to) {
    const metrics = manifest.metric[dataset];
    assertKnown('dataset', dataset, metrics);
    if (!metrics[metric]) return Promise.resolve([]);

    const wanted = new Set(isoCodes);
    return once(`metric:${dataset}:${metric}`, metrics[metric]).then(data => {
        const records = [];
        for (const iso of Object.keys(data.v)) {
            if (!wanted.has(iso)) continue;
            const meta = data.countries[iso];
            const dates = datesOf(meta);
            for (let i = 0; i < dates.length; i++) {
                if (dates[i] < from || dates[i] > to) continue;
                records.push({
                    date: dates[i] + SUFFIX,
                    iso_code: iso,
                    location: meta.location,
                    [metric]: data.v[iso][i]
                });
            }
        }
        return records;
    });
}

/** Date span of the country table, for the data table's date picker. */
export function countryDateRange(dataset) {
    const meta = index[dataset];
    assertKnown('dataset', dataset, meta);
    return { min: meta.countryDateMin, max: meta.countryDateMax };
}
